/* =============================================================================
   ics-textbox — the plain text field, and the INPUT-FAMILY CORE.
   It is both a widget in its own right and the base class the numeric and
   date/time fields extend. The subclasses are:
     Numberbox      (components/ics-numberbox.js)  - role numberbox
     Numberspinner  (same file, extends Numberbox) - role numberspinner
     Datetime       (components/ics-datetime.js, mix(Textbox, PopupMixin)) - the
                    date / datetime / time / month / year / calendar ROLES are
                    one class, not one class each
   so `.icsTextbox('readonly', true)` on any of them dispatches into the same
   instance API. Combo / Combotree / Autocomplete are NOT in this family (they
   extend their own base in ics-combo.js).
   Paint: lib-v2\css\components\ics-textbox.css.

   ENHANCE-IN-PLACE: the authored <input> IS the widget — no wrapper, no clone,
   no hidden twin, no sibling shell, and on the plain field NOT EVEN AN ADDED
   CLASS. What that guarantees a caller:
   - $('#id') keeps addressing the live, visible, editable element; .val(),
     .focus(), .prop('readonly'), `:input`, a keyup/autocomplete binding and a
     model binding on that input all keep working exactly as on a bare input;
   - the element's class attribute is byte-identical before and after the mount,
     so code that matches a class LIST or a whole class STRING (a validator that
     tests `attr('class') === 'required'`, for one) sees what it saw before;
   - inline styles authored on the input stay on the input — width, text-align
     and the like keep working; the face itself is the stylesheet's.
   The one mark the component leaves is the `data-ics="textbox"` attribute, which
   is also its mount selector and the stylesheet's paint hook: authored markup
   carries it already; a programmatic mount on a bare input adds it, and destroy()
   removes only what it added.

   AUTHORING (markup form)
     <input type="text" id="txtRemarks" data-ics="textbox" style="width:300px">
     <input type="text" data-ics="textbox" data-options="disabled:true">
     <input type="text" data-ics="textbox" readonly="readonly">

   OPTIONS (Textbox.defaults) — disabled, readonly. Resolved from the native
   attributes (PRESENCE counts — `readonly`, `readonly=""`, `disabled` all mean
   on), then data-* / data-options="k:v" (core/options.js, no eval), then the
   constructor argument. Both routes go through the very same methods a caller
   uses at runtime. width, placeholder, maxlength, value and the input type are
   the input's own native attributes and are never re-implemented here.

   METHOD CONTRACT (shared by the whole family; the facade dispatches method
   strings, and a mutator runs on EVERY matched element)
   - readonly(on = true): the no-argument form means true; idempotent. On the
     plain field the state is the native `readonly` attribute/property; on a
     composite field it is that plus the `ics-readonly` class the composite's
     shell reads.
   - disable() / enable(): the native `disabled` property; composite fields
     also toggle `ics-disabled`.
   - setValue(v): works while readonly, disabled or hidden; fires the component
     'change' (CustomEvent 'ics:change' {value, oldValue} + options.onChange)
     synchronously and only on a REAL change. It never fires the native DOM
     'change' event — a programmatic set is not a user edit.
   - getValue(): ALWAYS the live input value; an empty box returns '' (the DOM
     holds a string, so null/undefined never come back).
   - textbox(): the REAL focusable editing element as a raw node — for the
     plain field that is the element itself; composite fields inherit this and
     keep returning their own visible text input.
   - option(name[, value]) / 'options': read or patch the live options bag;
     patching `disabled` / `readonly` applies the state.
   ⚠ There is deliberately NO 'disabled' METHOD-STRING: the facade throws its
   unknown-method error for it, so a wrong call fails loudly instead of
   silently doing nothing. Use disable() / enable().

   MOUNTING
     Registry entry 'Textbox' — selector `[data-ics="textbox"]` (plus the older
     input markup class for a shell that carries no other implementation of
     it), facade `$.fn.icsTextbox`, and the short plugin name `textbox` where
     that name is free. A method-string call on an unmounted matching element
     mounts it first (facade/register.js). The constructor is idempotent, so a
     re-scan is a no-op.

   Out of scope by design: no validation engine, no prompt/label/icon/button
   surface, no multiline mode — the plain field is the input the page wrote.
   ============================================================================= */
import { Component } from '../core/component.js';
import { mix, FieldMixin } from '../core/mixins.js';
import { Registry } from '../services/registry.js';
/* isOn - the option-boolean truthiness gate (the quoted strings 'false' / '0'
   read as OFF). It lives in core/options.js beside coerce() and
   parseDataOptions(), the two functions that PRODUCE the values it reconciles;
   never keep a local copy here. */
import { resolveOptions, isOn } from '../core/options.js';

const OWN_ATTR = 'data-ics';
const OWN_NAME = 'textbox';

export class Textbox extends mix(Component, FieldMixin) {
  static defaults = { disabled: false, readonly: false };

  /**
   * defaults <- native attrs (readonly/disabled PRESENCE) <- data-* <- ctor
   *
   * Only the NATIVE-ATTRIBUTE tier is this class's own business; the data-* /
   * data-options / ctor tiers are the library's and are DELEGATED to the core
   * resolver. This static is inherited by the whole field family - Numberbox,
   * Numberspinner and Datetime (see the header) extend Textbox - so a copy here
   * would fork option precedence for the family and any tier later added to
   * the core would silently never reach them.
   * ⚠ The FLIP SIDE binds the subclasses: `this.defaults` here resolves to the
   * SUBCLASS's static, so a subclass must declare its defaults as
   * `Object.assign({}, Textbox.defaults, {...})` - a hand-copied
   * `disabled:false, readonly:false` freezes today's base keys, and any key
   * later added to Textbox.defaults would silently never be read from that
   * subclass's data- attributes.
   */
  static resolveOptions(el, options) {
    const attrs = {};
    if (el && el.hasAttribute) {
      if (el.hasAttribute('readonly')) attrs.readonly = true;
      if (el.hasAttribute('disabled')) attrs.disabled = true;
    }
    /* The native tier ranks ABOVE defaults and BELOW every other tier, and the
       core resolver has no seam there - so it is folded into the defaults the
       core starts from. Precedence is unchanged and the data / ctor tiers now
       have exactly one definition in the library.
       Both keys are already declared in defaults, so the set of data- attributes
       the core will read is untouched. */
    const base = { defaults: Object.assign({}, this.defaults, attrs) };
    return resolveOptions(base, el, options);
  }

  /**
   * The plain field is a DIRECT instance of this class; every composite field
   * is a subclass. The plain field keeps the element's class list untouched
   * and marks state through the native attributes only; a composite field
   * additionally carries the family state classes its own shell paints from.
   */
  get _plain() { return this.constructor === Textbox; }

  _init() {
    if (this._plain) {
      // the one mark: the own attribute, added only when the markup lacks it
      // (a programmatic mount on a bare input) and removed again on destroy
      if (this.el.getAttribute(OWN_ATTR) !== OWN_NAME) {
        this.el.setAttribute(OWN_ATTR, OWN_NAME);
        this._addedAttr = true;
      }
    } else {
      this.el.classList.add('ics-textbox');          // family base look for a composite's own input
    }
    // initial states, from data-options or the native attributes; both routes
    // go through the very same methods a caller uses at runtime
    if (isOn(this.options.disabled)) this.disable();
    if (isOn(this.options.readonly)) this.readonly(true);
    /* onChange is a DECLARED option and is resolvable from markup, so it has to
       be reachable from a USER edit as well as from the programmatic setValue
       path - this is the one event binding on the plain field. EventBinder owns
       the teardown.
       Two things this deliberately does NOT do:
       - it binds on the PLAIN field only. Every composite (Datetime, Numberbox,
         ...) subclasses this class and runs its own commit logic; binding here
         would make those emit 'change' twice.
       - it emits the SAME payload shape as FieldMixin.setValue, {value, oldValue},
         and only on a real change. One event name must mean one payload. The
         previous value is taken at focus, which is exactly "before this edit"
         and needs no coupling with setValue. */
    if (this._plain) {
      this._events.add(this.el, 'focus', () => { this._valueAtFocus = this.getValue(); });
      this._events.add(this.el, 'change', () => {
        const v = this.getValue(), old = this._valueAtFocus;
        this._valueAtFocus = v;
        if (String(old == null ? '' : old) !== String(v == null ? '' : v)) {
          this.emit('change', { value: v, oldValue: old });
        }
      });
    }
  }

  /**
   * Family accessor: the REAL focusable editing element, as a raw node.
   * For the plain field that is the element itself; composite fields
   * (the date/time roles etc.) inherit and keep returning their visible text input.
   */
  textbox() { return this._field(); }

  /** readonly(on = true): the no-argument form means true; idempotent.
      Plain field: native attribute only (the stylesheet paints [readonly]). */
  readonly(on = true) {
    this.options.readonly = !!on;                    // the bag is the state every path reads (a composite's trigger consults it)
    if (!this._plain) return super.readonly(on);
    const f = this._field();
    if (f) f.readOnly = !!on;
    return this;
  }

  disable() {
    this.options.disabled = true;
    if (!this._plain) return super.disable();
    const f = this._field();
    if (f) f.disabled = true;
    return this;
  }

  enable() {
    this.options.disabled = false;
    if (!this._plain) return super.enable();
    const f = this._field();
    if (f) f.disabled = false;
    return this;
  }

  /** option('disabled' | 'readonly', v) applies the state; other keys are data */
  _optionChanged(name, value) {
    if (name === 'disabled') { isOn(value) ? this.disable() : this.enable(); return; }
    if (name === 'readonly') { this.readonly(isOn(value)); }
  }

  /** teardown removes only what the mount added: the own attribute on a
      programmatically mounted bare input, and - on a COMPOSITE field - the
      ics-textbox class the mount put on its inner input. The element, its value
      and its STATE classes are left exactly as they are (deliberate: a field
      that was disabled stays visibly disabled after teardown). */
  _destroy() {
    if (this._plain && this._addedAttr) {
      this.el.removeAttribute(OWN_ATTR);
      this._addedAttr = false;
    }
    if (!this._plain) this.el.classList.remove('ics-textbox');   // mirrors the add in _init
  }

  /* setValue / getValue come from FieldMixin:
     - setValue: works while readonly/disabled/hidden; fires 'change' only on a
       REAL change, synchronously; never the native DOM 'change' event.
     - getValue: ALWAYS-LIVE input value; empty box -> '' (null/undefined can
       never come back, because the DOM input holds a string). */
}

/* Auto-mount + facade. The selector matches <input> elements only, on the own
   attribute form (the older `easyui-textbox` markup-class arm retired with the
   vendor library 2026-09-02 - zero carriers estate-wide). The arm excludes an
   input another field implementation has already built on (its engine stamp
   `textbox-f` is on the element): that input is hidden behind that
   implementation's own shell and is never ours. */
Registry.add('Textbox', Textbox, {
  selector: 'input[data-ics="textbox"]:not(.textbox-f)',
  claimName: 'textbox',                 // also answers the short plugin name $.fn.textbox
});
